// Derives a Tailwind-style shade scale from a single brand color and
// applies it as CSS custom properties, so a shop can look distinct
// without a separate frontend build per bot. `--brand-600` is exactly
// the configured color; the rest are lighter/darker tints at the same
// hue/saturation, matching Tailwind's own shade-lightness spacing.
const SHADE_LIGHTNESS: Record<string, number> = {
  '50': 0.97,
  '100': 0.94,
  '200': 0.86,
  '300': 0.74,
  '400': 0.55,
  '500': 0.4,
  '700': 0.2,
}

function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const match = /^#([0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return null
  const int = parseInt(match[1], 16)
  const r = ((int >> 16) & 255) / 255
  const g = ((int >> 8) & 255) / 255
  const b = (int & 255) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  switch (max) {
    case r:
      h = (g - b) / d + (g < b ? 6 : 0)
      break
    case g:
      h = (b - r) / d + 2
      break
    default:
      h = (r - g) / d + 4
  }
  return { h: h / 6, s, l }
}

function hslToHex(h: number, s: number, l: number): string {
  if (s === 0) {
    const v = Math.round(l * 255)
    return `#${v.toString(16).padStart(2, '0').repeat(3)}`
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t
    if (tt < 0) tt += 1
    if (tt > 1) tt -= 1
    if (tt < 1 / 6) return p + (q - p) * 6 * tt
    if (tt < 1 / 2) return q
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
    return p
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const r = hue2rgb(p, q, h + 1 / 3)
  const g = hue2rgb(p, q, h)
  const b = hue2rgb(p, q, h - 1 / 3)
  const toHex = (v: number) => Math.round(v * 255).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

export function applyBrandTheme(primaryHex: string | null | undefined): void {
  if (!primaryHex) return
  const hsl = hexToHsl(primaryHex)
  if (!hsl) return
  const root = document.documentElement.style
  root.setProperty('--brand-600', primaryHex)
  for (const [shade, lightness] of Object.entries(SHADE_LIGHTNESS)) {
    root.setProperty(`--brand-${shade}`, hslToHex(hsl.h, hsl.s, lightness))
  }
}
